import type { TableView } from 'owa-mail-list-store';
import { MailRowDataPropertyGetter } from 'owa-mail-list-store';
import { getTimestamp, userDate, isSameDay } from 'owa-datetime';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { getUserStore } from '../store/store';
import getLastPrioritizeNudgeTimestamp from 'owa-mail-copilot-settings/lib/selectors/getLastPrioritizeNudgeTimestamp';

/**
 * Gets the row keys to prioritize from the given table view.
 * @param tableView The table view to get the row keys from.
 * @isForNudge If true, use the last time the nudge was dismissed OR Prioritize was used
 */
export function getRowsToPrioritize(tableView: TableView, isForNudge?: boolean): string[] {
    const rowKeys = [...tableView.rowKeys];

    const mailboxInfo = getMailboxInfo(tableView);
    const store = getUserStore(mailboxInfo);
    const nudgeTimestamp = getLastPrioritizeNudgeTimestamp(mailboxInfo);
    const currentTimestamp = getTimestamp(userDate());
    const hasValidNudgeTimestamp =
        nudgeTimestamp !== null && nudgeTimestamp > 0 && nudgeTimestamp <= currentTimestamp;

    // If the nudge was already shown today, don't show it again.
    if (
        isForNudge &&
        hasValidNudgeTimestamp &&
        nudgeTimestamp !== null &&
        isSameDay(userDate(nudgeTimestamp), userDate())
    ) {
        return [];
    }

    return rowKeys.filter(rowKey => {
        // Filter out email that has already been prioritized and skipped
        if (store.skippedRowKeys.includes(rowKey)) {
            return false;
        }

        // Filter out email that is read
        const isUnread = MailRowDataPropertyGetter.getUnreadCount(rowKey, tableView) > 0;
        if (isUnread === false) {
            return false;
        }

        // Filter out email that has already been prioritized
        const isPrioritized = MailRowDataPropertyGetter.getCopilotInboxScore(rowKey, tableView) > 0;
        if (isPrioritized) {
            return false;
        }

        // - When prioritizing, filter out email that is older than 24 hours ago
        // - For nudge, filter out email older than last nudge timestamp (dismiss time or last
        // prioritize time)
        const receivedTime = MailRowDataPropertyGetter.getLastDeliveryTimeStamp(rowKey, tableView);
        if (!receivedTime) {
            return false;
        }
        const now = new Date();
        const isMonday = now.getDay() === 1;
        const lookbackDays = isMonday ? 3 : 1;
        const filterTime = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() - lookbackDays
        );

        const filterTimestamp =
            isForNudge &&
            hasValidNudgeTimestamp &&
            nudgeTimestamp !== null &&
            nudgeTimestamp > filterTime.getTime()
                ? nudgeTimestamp
                : filterTime.getTime();

        return new Date(receivedTime).getTime() >= filterTimestamp;
    });
}
